/** Mřížka karet produktů se stavy: načítání (skeleton), prázdno, chyba. */

import { useEffect } from 'react';
import { toast } from 'sonner';
import type { ProductList } from '../lib/api';
import ProductCard from './ProductCard';

function SkeletonCard() {
  return (
    <div className="flex flex-col overflow-hidden rounded-card border border-line bg-card shadow-card">
      <div className="aspect-square bg-paper2 motion-safe:animate-pulse" />
      <div className="flex flex-col gap-2.5 p-4 pb-5">
        <div className="h-3 w-24 rounded bg-paper2 motion-safe:animate-pulse" />
        <div className="h-4 w-full rounded bg-paper2 motion-safe:animate-pulse" />
        <div className="h-4 w-2/3 rounded bg-paper2 motion-safe:animate-pulse" />
        <div className="mt-2 flex items-center justify-between border-t border-line pt-3.5">
          <div className="h-5 w-16 rounded bg-paper2 motion-safe:animate-pulse" />
          <div className="h-9 w-9 rounded-xl bg-paper2 motion-safe:animate-pulse" />
        </div>
      </div>
    </div>
  );
}

/** Prázdná tabule s houbou, křídová ilustrace místo generické lucide ikony. */
function EmptyBoard() {
  return (
    <div className="board mx-auto grid h-24 w-32 place-items-center rounded-xl">
      <svg viewBox="0 0 120 80" fill="none" aria-hidden="true" className="h-16 w-24">
        <g
          stroke="#F3EFE4"
          strokeWidth={2.2}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.9}
        >
          <rect x="14" y="12" width="92" height="48" rx="3" />
          <rect
            x="70"
            y="46"
            width="26"
            height="12"
            rx="2"
            fill="#F6C445"
            stroke="#F6C445"
            opacity={0.85}
          />
          <path d="M28 30 h30 M28 40 h20" opacity={0.55} />
        </g>
      </svg>
    </div>
  );
}

export default function ProductGrid({
  data,
  isLoading,
  isError,
  error,
  skeletonCount = 6,
  emptyLabel = 'Zatím tu nic není',
}: {
  data: ProductList | undefined;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  skeletonCount?: number;
  emptyLabel?: string;
}) {
  useEffect(() => {
    if (isError) {
      const message = error instanceof Error ? error.message : 'Nepodařilo se načíst produkty.';
      toast.error('Katalog se nepodařilo načíst', { description: message });
    }
  }, [isError, error]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-card border border-line bg-card p-10 text-center">
        <p className="font-serif text-xl">Katalog se nepodařilo načíst</p>
        <p className="mt-2 text-sm text-ink-soft">
          Zkuste to prosím za chvíli znovu, server možná chvíli neodpovídá.
        </p>
      </div>
    );
  }

  const items = data?.items ?? [];
  if (items.length === 0) {
    return (
      <div className="rounded-card border border-line bg-card p-10 text-center">
        <EmptyBoard />
        <p className="mt-4 font-serif text-xl">{emptyLabel}</p>
        <p className="mt-1.5 text-sm text-ink-soft">Zkuste změnit filtr nebo hledaný výraz.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
      {items.map((product, i) => (
        <ProductCard key={product.id} product={product} index={i} />
      ))}
    </div>
  );
}
